'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface RegisterFormData {
  fullName: string;
  hallTicket: string;
  email: string;
  phone: string;
  department: string;
  year: string;
  awsBuilderId: string;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
}

interface RegisterFormProps {
  onSwitchToLogin: () => void;
}

const DEPARTMENTS = ['CSE', 'ECE', 'AIML', 'EEE', 'CIVIL'];

const YEARS = ['1st Year', '2nd Year', '3rd Year', '4th Year'];

export default function RegisterForm({ onSwitchToLogin }: RegisterFormProps) {
  const { signUp } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<RegisterFormData>();

  const password = watch('password');

  const onSubmit = async (data: RegisterFormData) => {
    // Validate department
    if (!DEPARTMENTS.includes(data.department)) {
      setError('department', { message: 'Please select a valid department' });
      return;
    }

    setIsLoading(true);
    try {
      await signUp(data.email, data.password, {
        fullName: data.fullName,
        hallTicket: data.hallTicket,
        phone: data.phone,
        department: data.department,
        year: data.year,
        awsBuilderId: data.awsBuilderId || '',
      });
      setIsLoading(false);
      setSubmitted(true);
      toast.success('Registration submitted! Awaiting admin approval.');
    } catch (err: any) {
      const msg = err?.message || '';
      if (msg.toLowerCase().includes('already registered') || msg.toLowerCase().includes('already exists')) {
        setError('email', { message: 'This email is already registered. Try signing in.' });
      } else {
        setError('root', { message: msg || 'Registration failed. Please try again.' });
      }
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-8 fade-in">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
          style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)' }}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2.5">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h3 className="text-xl font-700 mb-2" style={{ color: 'var(--foreground)' }}>
          Registration Submitted!
        </h3>
        <p className="text-sm mb-1" style={{ color: 'var(--muted-foreground)' }}>
          Your account is pending approval by the AWS Club Lead.
        </p>
        <p className="text-sm mb-6" style={{ color: 'var(--muted-foreground)' }}>
          You will be notified once your account is activated.
        </p>
        <button
          onClick={onSwitchToLogin}
          className="btn-primary px-6 py-2.5 rounded-xl text-sm font-600"
        >
          Back to Sign In
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-2xl font-700" style={{ color: 'var(--foreground)' }}>
          Join AWS Club
        </h2>
        <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>
          Create your student member account
        </p>
      </div>

      {errors.root && (
        <div
          className="flex items-center gap-2 px-4 py-3 rounded-xl mb-4 fade-in"
          style={{ background: 'var(--negative-bg)', border: '1px solid rgba(239,68,68,0.3)' }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          <p className="text-sm" style={{ color: '#ef4444' }}>{errors.root.message}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Full name */}
        <div>
          <label htmlFor="reg-name" className="block text-sm font-500 mb-1.5" style={{ color: 'var(--foreground)' }}>
            Full Name <span style={{ color: 'var(--negative)' }}>*</span>
          </label>
          <input
            id="reg-name"
            type="text"
            placeholder="Arjun Reddy"
            className="w-full px-4 py-2.5 rounded-xl text-sm"
            style={{
              background: 'var(--input)',
              border: `1px solid ${errors.fullName ? 'var(--negative)' : 'var(--border)'}`,
              color: 'var(--foreground)',
              outline: 'none',
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = 'var(--primary)';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.15)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = errors.fullName ? 'var(--negative)' : 'var(--border)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            {...register('fullName', { required: 'Full name is required' })}
          />
          {errors.fullName && (
            <p className="text-xs mt-1" style={{ color: 'var(--negative)' }}>{errors.fullName.message}</p>
          )}
        </div>

        {/* Hall ticket + year row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="reg-hall" className="block text-sm font-500 mb-1.5" style={{ color: 'var(--foreground)' }}>
              Hall Ticket No. <span style={{ color: 'var(--negative)' }}>*</span>
            </label>
            <input
              id="reg-hall"
              type="text"
              placeholder="21B01A0501"
              className="w-full px-4 py-2.5 rounded-xl text-sm"
              style={{
                background: 'var(--input)',
                border: `1px solid ${errors.hallTicket ? 'var(--negative)' : 'var(--border)'}`,
                color: 'var(--foreground)',
                outline: 'none',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'var(--primary)';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.15)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = errors.hallTicket ? 'var(--negative)' : 'var(--border)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              {...register('hallTicket', {
                required: 'Hall ticket required',
                pattern: { value: /^[0-9A-Za-z]{8,12}$/, message: 'Invalid format' },
              })}
            />
            {errors.hallTicket && (
              <p className="text-xs mt-1" style={{ color: 'var(--negative)' }}>{errors.hallTicket.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="reg-year" className="block text-sm font-500 mb-1.5" style={{ color: 'var(--foreground)' }}>
              Year <span style={{ color: 'var(--negative)' }}>*</span>
            </label>
            <select
              id="reg-year"
              className="w-full px-4 py-2.5 rounded-xl text-sm"
              style={{
                background: 'var(--input)',
                border: `1px solid ${errors.year ? 'var(--negative)' : 'var(--border)'}`,
                color: 'var(--foreground)',
                outline: 'none',
              }}
              {...register('year', { required: 'Year is required' })}
            >
              <option value="">Select</option>
              {YEARS.map((y) => (
                <option key={`year-${y}`} value={y}>{y}</option>
              ))}
            </select>
            {errors.year && (
              <p className="text-xs mt-1" style={{ color: 'var(--negative)' }}>{errors.year.message}</p>
            )}
          </div>
        </div>

        {/* Email */}
        <div>
          <label htmlFor="reg-email" className="block text-sm font-500 mb-1.5" style={{ color: 'var(--foreground)' }}>
            College Email <span style={{ color: 'var(--negative)' }}>*</span>
          </label>
          <input
            id="reg-email"
            type="email"
            placeholder="21b01a0501@college.edu.in"
            className="w-full px-4 py-2.5 rounded-xl text-sm"
            style={{
              background: 'var(--input)',
              border: `1px solid ${errors.email ? 'var(--negative)' : 'var(--border)'}`,
              color: 'var(--foreground)',
              outline: 'none',
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = 'var(--primary)';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.15)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = errors.email ? 'var(--negative)' : 'var(--border)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /\S+@\S+\.\S+/, message: 'Enter a valid email' },
            })}
          />
          {errors.email && (
            <p className="text-xs mt-1" style={{ color: 'var(--negative)' }}>{errors.email.message}</p>
          )}
        </div>

        {/* Phone + Department */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="reg-phone" className="block text-sm font-500 mb-1.5" style={{ color: 'var(--foreground)' }}>
              Phone <span style={{ color: 'var(--negative)' }}>*</span>
            </label>
            <input
              id="reg-phone"
              type="tel"
              placeholder="+91 98765 43210"
              className="w-full px-4 py-2.5 rounded-xl text-sm"
              style={{
                background: 'var(--input)',
                border: `1px solid ${errors.phone ? 'var(--negative)' : 'var(--border)'}`,
                color: 'var(--foreground)',
                outline: 'none',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'var(--primary)';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.15)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = errors.phone ? 'var(--negative)' : 'var(--border)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              {...register('phone', { required: 'Phone required' })}
            />
            {errors.phone && (
              <p className="text-xs mt-1" style={{ color: 'var(--negative)' }}>{errors.phone.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="reg-dept" className="block text-sm font-500 mb-1.5" style={{ color: 'var(--foreground)' }}>
              Department <span style={{ color: 'var(--negative)' }}>*</span>
            </label>
            <select
              id="reg-dept"
              className="w-full px-4 py-2.5 rounded-xl text-sm"
              style={{
                background: 'var(--input)',
                border: `1px solid ${errors.department ? 'var(--negative)' : 'var(--border)'}`,
                color: 'var(--foreground)',
                outline: 'none',
              }}
              {...register('department', {
                required: 'Department required',
                validate: (v) => DEPARTMENTS.includes(v) || 'Select a valid department',
              })}
            >
              <option value="">Select dept.</option>
              {DEPARTMENTS.map((d) => (
                <option key={`dept-${d}`} value={d}>{d}</option>
              ))}
            </select>
            {errors.department && (
              <p className="text-xs mt-1" style={{ color: 'var(--negative)' }}>{errors.department.message}</p>
            )}
          </div>
        </div>

        {/* AWS Builder ID */}
        <div>
          <label htmlFor="reg-aws" className="block text-sm font-500 mb-1" style={{ color: 'var(--foreground)' }}>
            AWS Builder ID
          </label>
          <p className="text-xs mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
            Your unique ID from AWS Builder community (optional but recommended)
          </p>
          <input
            id="reg-aws"
            type="text"
            placeholder="amzn1.builderid.xxxxxxxx"
            className="w-full px-4 py-2.5 rounded-xl text-sm font-mono"
            style={{
              background: 'var(--input)',
              border: '1px solid var(--border)',
              color: 'var(--foreground)',
              outline: 'none',
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = 'var(--primary)';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.15)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'var(--border)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            {...register('awsBuilderId')}
          />
        </div>

        {/* Password */}
        <div>
          <label htmlFor="reg-password" className="block text-sm font-500 mb-1.5" style={{ color: 'var(--foreground)' }}>
            Password <span style={{ color: 'var(--negative)' }}>*</span>
          </label>
          <div className="relative">
            <input
              id="reg-password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Min. 8 characters"
              className="w-full px-4 py-2.5 pr-11 rounded-xl text-sm"
              style={{
                background: 'var(--input)',
                border: `1px solid ${errors.password ? 'var(--negative)' : 'var(--border)'}`,
                color: 'var(--foreground)',
                outline: 'none',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'var(--primary)';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.15)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = errors.password ? 'var(--negative)' : 'var(--border)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Minimum 8 characters' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1"
              style={{ color: 'var(--muted-foreground)' }}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
          {errors.password && (
            <p className="text-xs mt-1" style={{ color: 'var(--negative)' }}>{errors.password.message}</p>
          )}
        </div>

        {/* Confirm password */}
        <div>
          <label htmlFor="reg-confirm" className="block text-sm font-500 mb-1.5" style={{ color: 'var(--foreground)' }}>
            Confirm Password <span style={{ color: 'var(--negative)' }}>*</span>
          </label>
          <input
            id="reg-confirm"
            type="password"
            placeholder="Re-enter password"
            className="w-full px-4 py-2.5 rounded-xl text-sm"
            style={{
              background: 'var(--input)',
              border: `1px solid ${errors.confirmPassword ? 'var(--negative)' : 'var(--border)'}`,
              color: 'var(--foreground)',
              outline: 'none',
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = 'var(--primary)';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.15)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = errors.confirmPassword ? 'var(--negative)' : 'var(--border)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            {...register('confirmPassword', {
              required: 'Please confirm your password',
              validate: (val) => val === password || 'Passwords do not match',
            })}
          />
          {errors.confirmPassword && (
            <p className="text-xs mt-1" style={{ color: 'var(--negative)' }}>{errors.confirmPassword.message}</p>
          )}
        </div>

        {/* Terms */}
        <div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 w-4 h-4 rounded flex-shrink-0"
              style={{ accentColor: 'var(--primary)' }}
              {...register('agreeTerms', { required: 'You must accept the terms' })}
            />
            <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
              I agree to the{' '}
              <span className="font-500 cursor-pointer" style={{ color: 'var(--secondary-foreground)' }}>
                Club Code of Conduct
              </span>{' '}
              and{' '}
              <span className="font-500 cursor-pointer" style={{ color: 'var(--secondary-foreground)' }}>
                Privacy Policy
              </span>
            </span>
          </label>
          {errors.agreeTerms && (
            <p className="text-xs mt-1" style={{ color: 'var(--negative)' }}>{errors.agreeTerms.message}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full py-3 rounded-xl text-sm font-600 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
          style={{ minHeight: '44px' }}
        >
          {isLoading ? (
            <>
              <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              Submitting registration...
            </>
          ) : (
            'Register as Student Member'
          )}
        </button>
      </form>

      <p className="text-sm text-center mt-4" style={{ color: 'var(--muted-foreground)' }}>
        Already a member?{' '}
        <button
          onClick={onSwitchToLogin}
          className="font-600"
          style={{ color: 'var(--secondary-foreground)' }}
        >
          Sign in
        </button>
      </p>
    </div>
  );
}